import { EllipsisTooltip } from '@mpc/components';
import React, { cloneElement, isValidElement, ReactElement, ReactNode } from 'react';
import {
  decideDiffRender,
  isBusinessAreaValue,
  joinBusinessArea,
} from './businessAreaText';

export { joinBusinessArea, isBusinessAreaValue };
export type { BusinessAreaValue } from './businessAreaText';

/**
 * 归一行政区划展示值。
 *
 * diff 态会传入已渲染节点或字符串，此时原样透传，避免被二次渲染成空。
 */
export const formatBusinessArea = (
  value: unknown,
  isOverseas: boolean
): ReactNode => {
  if (isValidElement(value) || typeof value === 'string') {
    return value;
  }
  if (!isBusinessAreaValue(value)) {
    return '-';
  }
  return joinBusinessArea(value, isOverseas);
};

/**
 * 构造行政区划 FormItem 的 render 覆盖。
 *
 * 适用于包内所有走同一拼接口径的地区字段：
 * 「主要经营地区」FormItemBusinessArea 与「其他经营地区」FormItemBranchInstitutionArea。
 *
 * `@purchase/supplier` 的 FormItem 以 `{ ...defaultProps, ...props }` 合并 props，
 * 传入的 render 会覆盖包内默认实现；因此需自行补回默认实现里的 collapsed 省略号处理。
 *
 * @param isOverseas 是否海外供应商
 * @param collapsed 是否折叠态（对应 FormDescriptions 的 collapsed）
 */
export const createBusinessAreaRender =
  (isOverseas: boolean, collapsed: boolean) =>
  (value: unknown): ReactNode => {
    const content = formatBusinessArea(value, isOverseas);
    return collapsed ? <EllipsisTooltip clamp={1} text={content} /> : content;
  };

/**
 * 构造行政区划 FormItem 的 renderFormItem 覆盖，供变更记录（diff）态使用。
 *
 * 适用于「主要经营地区」与「其他经营地区」两处 —— 包内两个 FormItem 的 diff 分支
 * 写法相同，都直接用原始值走各自包内渲染。
 *
 * 背景：包内默认 `renderFormItem` 的 diff 分支用的是 `dom.props.oldValue/newValue`
 * 原始值、直接走包内 BusinessArea 渲染，**不经过调用方传入的 render**；
 * 因此只覆盖 render 无法影响变更记录的展示，必须一并覆盖 renderFormItem。
 *
 * 结构复刻包内 diff 分支（cloneElement 注入已渲染节点），
 * 差别仅在于注入值改由 decideDiffRender 计算（海外只展示省/州）。
 * 非 diff 态返回 undefined，交回包内默认实现，行为不变。
 *
 * @param isOverseas 是否海外供应商
 */
export const createBusinessAreaDiffRenderFormItem =
  (isOverseas: boolean) =>
  (
    /** 当前值：diff 态下注入值取自 dom.props，此参数不参与计算，保留位置以匹配包内签名 */
    _value: unknown,
    props: { mode?: string },
    dom: ReactElement
  ): ReactNode => {
    const decision = decideDiffRender(props?.mode, dom.props, isOverseas);
    if (decision.kind === 'change') {
      return cloneElement(dom, {
        oldValue: decision.oldValue,
        newValue: decision.newValue,
      });
    }
    if (decision.kind === 'single') {
      return cloneElement(dom, { value: decision.value });
    }
    return undefined;
  };
